import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { SectionActionButton } from '@/store/components/SectionActionButton'
import { homeEase, homeFadeUp } from './motion'

interface HomeSectionProps {
  id?: string
  eyebrow?: string
  title?: string
  subtitle?: string
  actionHref?: string
  actionLabel?: string
  children: React.ReactNode
  className?: string
  headerClassName?: string
  align?: 'left' | 'center'
  size?: 'default' | 'compact' | 'hero'
}

const sizePadding: Record<NonNullable<HomeSectionProps['size']>, string> = {
  hero: 'py-0',
  default: 'py-16 md:py-24',
  compact: 'py-12 md:py-16',
}

export function HomeSection({
  id,
  eyebrow,
  title,
  subtitle,
  actionHref,
  actionLabel = 'View all',
  children,
  className,
  headerClassName,
  align = 'left',
  size = 'default',
}: HomeSectionProps) {
  const reduceMotion = useReducedMotion()

  return (
    <section
      id={id}
      className={cn('relative', sizePadding[size], className)}
      aria-labelledby={title ? `${id ?? 'section'}-title` : undefined}
    >
      <div className="store-container">
        {(eyebrow || title || subtitle) && (
          <motion.header
            initial={reduceMotion ? false : 'hidden'}
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={homeFadeUp}
            transition={{ duration: 0.5, ease: homeEase }}
            className={cn(
              'mb-10 md:mb-12',
              !actionHref && 'max-w-3xl',
              align === 'center' && 'mx-auto text-center',
              headerClassName
            )}
          >
            {eyebrow && (
              <p className="type-eyebrow mb-3">{eyebrow}</p>
            )}
            {title && (
              <div
                className={cn(
                  'flex items-center gap-3 sm:gap-4',
                  actionHref ? 'justify-between' : align === 'center' && 'justify-center'
                )}
              >
                <h2
                  id={`${id ?? 'section'}-title`}
                  className={cn(
                    'type-section-title min-w-0 text-3xl md:text-4xl lg:text-5xl',
                    actionHref && 'flex-1',
                    align === 'center' && !actionHref && 'text-center'
                  )}
                >
                  {title}
                </h2>
                {actionHref && (
                  <SectionActionButton to={actionHref} animateIn={false}>
                    {actionLabel}
                  </SectionActionButton>
                )}
              </div>
            )}
            {subtitle && (
              <p className="mt-4 max-w-3xl type-body-muted text-base md:text-lg">{subtitle}</p>
            )}
          </motion.header>
        )}
        {children}
      </div>
    </section>
  )
}
